/**
 * Borrower organization data builder utilities
 * Constructs complex organization mutation objects for create/update operations
 */

import { Bank } from "../../../types/org.type";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import {
  CompanyRole,
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { Industry } from "../../../constants/enums";

/**
 * Bank state from Redux
 */
export interface BorrowerBankState {
  accountType: string | null;
  bankName: string | null;
  bankCode: string | null;
  accountNumber: string | null;
  swiftCode: string | null;
  accountHolderName: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

/**
 * Business state from Redux
 */
export interface BorrowerBizState {
  company: string | null;
  legalStructure: string | null;
  legalStructureOthers: string | null;
  regNumber: string | null;
  industryGroup: string | null;
  typeOfInstitute: string | null;
  industryOthers: string | null;
  countryOfDomicile: string | null;
  countryOfIncorp: string | null;
  region: string | null;
  city: string | null;
  postal: string | null;
  address: string | null;
  website: string | null;
  phoneNumber: string | null;
  incorporationDate: string | null;
}

/**
 * Representative state from Redux
 */
export interface BorrowerRepState {
  repFirstName: string | null;
  repLastName: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  repTitle: string | null;
  shareholdersDirectors: ShareholderDirector[];
  merchantTitle: string | null;
  repEmail: string | null;
}

/**
 * Shareholder/Director type
 */
export interface ShareholderDirector {
  firstName: string;
  lastName: string;
  email: string;
  role: CompanyRole;
}

/**
 * Build Bank object from Redux state
 */
export const buildBorrowerBankObject = (bankState: BorrowerBankState): Bank => {
  return {
    bankName: bankState.bankName ?? "",
    bankCode: bankState.bankCode || "",
    swiftCode: bankState.swiftCode ?? "",
    accountNo: bankState.accountNumber ?? "",
    accountType: (bankState.accountType ?? "").toLowerCase(),
    accountHolderName: bankState.accountHolderName ?? "",
    bankAddress: bankState.bankAddress ?? "",
    bankCountry: bankState.bankCountry ?? "",
    currency: bankState.currency ?? "",
  };
};

/**
 * Map shareholders from shareholdersDirectors
 */
export const mapBorrowerShareholders = (
  shareholdersDirectors: ShareholderDirector[]
) => {
  const shareholders = shareholdersDirectors.filter(
    (user) => user.role === CompanyRole.SHAREHOLDER
  );

  return shareholders.map((shareholder) => ({
    firstName: shareholder.firstName,
    lastName: shareholder.lastName,
    email: shareholder.email.toLowerCase(),
  }));
};

/**
 * Map directors from shareholdersDirectors
 */
export const mapBorrowerDirectors = (
  shareholdersDirectors: ShareholderDirector[],
  repEmail: string | null
) => {
  const directors = shareholdersDirectors.filter(
    (user) =>
      user.role === CompanyRole.BOTH || user.role === CompanyRole.DIRECTOR
  );

  return directors.map((director) => ({
    firstName: director.firstName,
    lastName: director.lastName,
    email: director.email.toLowerCase(),
    isShareholder: director.role === CompanyRole.BOTH,
    isAuthRep: director.email.toLowerCase() === repEmail?.toLowerCase(),
  }));
};

/**
 * Base organization data shared between create and update
 */
export interface BaseBorrowerOrgData {
  token: string;
  bank: Bank;
  documentsUpload: DocumentsUpload;
  firstName: string;
  lastName: string;
  jobTitle: string;
  companyName: string;
  companyRegistrationNo: string;
  companyStartDate: string;
  legalStructure: string;
  industryGroup: string;
  industry: string;
  countryISOCode: string | null;
  region: string | null;
  city: string | null;
  countryOfIncorporation: string;
  companyPhoneNumber: string;
  nameOfAuthorisedRepresentiative: string;
  legalEntityAddress: string;
  postalCode: string;
  titleOfAuthorisedRepresentiative: string;
  emailOfAuthorisedRepresentiative: string;
  onBoardingUserType: string;
  website: string;
  shareHolders: { firstName: string; lastName: string; email: string }[];
  boardOfDirectors: {
    firstName: string;
    lastName: string;
    email: string;
    isShareholder: boolean;
    isAuthRep: boolean;
  }[];
}

/**
 * Build base organization data object
 */
export const buildBaseBorrowerOrgData = (params: {
  bizState: BorrowerBizState;
  repState: BorrowerRepState;
  bank: Bank;
  token: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}): BaseBorrowerOrgData => {
  const { bizState, repState, bank, token, countryCode, documentsUpload } =
    params;

  return {
    token,
    bank,
    documentsUpload,
    firstName: repState.userFirstName ?? "",
    lastName: repState.userLastName ?? "",
    jobTitle: repState.merchantTitle ?? "",
    companyName: bizState.company ?? "",
    companyRegistrationNo: bizState.regNumber ?? "",
    companyStartDate: bizState.incorporationDate ?? "",
    legalStructure: bizState.legalStructure ?? "",
    industryGroup: bizState.industryGroup ?? "",
    industry:
      bizState.typeOfInstitute === Industry.OTHERS
        ? (bizState.industryOthers ?? "")
        : (bizState.typeOfInstitute ?? ""),
    countryISOCode: countryCode,
    region: bizState.region,
    city: bizState.city,
    countryOfIncorporation: bizState.countryOfIncorp ?? "",
    companyPhoneNumber: bizState.phoneNumber ?? "",
    nameOfAuthorisedRepresentiative: `${repState.repFirstName ?? ""} ${repState.repLastName ?? ""}`.trim(),
    legalEntityAddress: bizState.address ?? "",
    postalCode: bizState.postal ?? "",
    titleOfAuthorisedRepresentiative: repState.repTitle ?? "",
    emailOfAuthorisedRepresentiative: repState.repEmail?.toLowerCase() ?? "",
    onBoardingUserType: Persona.MERCHANT,
    website: bizState.website ?? "",
    shareHolders: mapBorrowerShareholders(repState.shareholdersDirectors),
    boardOfDirectors: mapBorrowerDirectors(
      repState.shareholdersDirectors,
      repState.repEmail
    ),
  };
};

/**
 * Build create organization data
 */
export const buildCreateBorrowerOrgData = (params: {
  bizState: BorrowerBizState;
  repState: BorrowerRepState;
  bank: Bank;
  token: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}) => {
  return buildBaseBorrowerOrgData(params);
};

/**
 * Build update organization data
 */
export const buildUpdateBorrowerOrgData = (params: {
  bizState: BorrowerBizState;
  repState: BorrowerRepState;
  bank: Bank;
  token: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}) => {
  return {
    ...buildBaseBorrowerOrgData(params),
    kycStatus: KYCStatus.PENDING_APPROVAL, // Update includes KYC status reset
  };
};
